/**
 * Centralized color system for the entire application.
 * Used by both MUI and Tailwind CSS.
 *
 * Each page has:
 *   primary   – used on light backgrounds (text, borders, accents)
 *   secondary – a lighter variant of primary
 *   accent    – a bright variant for dark backgrounds (text, icons, chips)
 *   gradient     – hero section backgrounds for light mode (paired with white text)
 *   darkGradient – muted hero backgrounds for dark mode (same hue, lower intensity)
 */

export interface PageColorConfig {
  readonly primary: string;
  readonly secondary: string;
  readonly accent: string;
  readonly gradient: string;
  readonly darkGradient: string;
  readonly name: string;
}

export const pageColors: Record<string, PageColorConfig> = {
  home: {
    primary: '#1e293b',
    secondary: '#334155',
    accent: '#94a3b8',
    gradient: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    darkGradient: 'linear-gradient(135deg, #0f1724 0%, #1e293b 100%)',
    name: 'Charcoal Blue',
  },
  about: {
    primary: '#1e3a8a',
    secondary: '#1e40af',
    accent: '#60a5fa',
    gradient: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
    darkGradient: 'linear-gradient(135deg, #122560 0%, #16307a 100%)',
    name: 'Navy',
  },
  music: {
    primary: '#1DB954',
    secondary: '#1ed760',
    accent: '#4ade80',
    gradient: 'linear-gradient(135deg, #1DB954 0%, #1ed760 100%)',
    darkGradient: 'linear-gradient(135deg, #0d7a35 0%, #15a048 100%)',
    name: 'Spotify Green',
  },
  blog: {
    primary: '#0f766e',
    secondary: '#14b8a6',
    accent: '#2dd4bf',
    gradient: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
    darkGradient: 'linear-gradient(135deg, #0a524c 0%, #0d8a7d 100%)',
    name: 'Teal',
  },
  quotes: {
    primary: '#7e22ce',
    secondary: '#a855f7',
    accent: '#c084fc',
    gradient: 'linear-gradient(135deg, #7e22ce 0%, #a855f7 100%)',
    darkGradient: 'linear-gradient(135deg, #5b1a96 0%, #7e3ebf 100%)',
    name: 'Purple',
  },
  projects: {
    primary: '#06b6d4',
    secondary: '#22d3ee',
    accent: '#67e8f9',
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)',
    darkGradient: 'linear-gradient(135deg, #047a91 0%, #0899b3 100%)',
    name: 'Cyan',
  },
  prodeng: {
    primary: '#b45309',
    secondary: '#d97706',
    accent: '#fbbf24',
    gradient: 'linear-gradient(135deg, #92400e 0%, #b45309 100%)',
    darkGradient: 'linear-gradient(135deg, #6b2f04 0%, #92400e 100%)',
    name: 'Amber',
  },
  uses: {
    primary: '#c2410c',
    secondary: '#ea580c',
    accent: '#fb923c',
    gradient: 'linear-gradient(135deg, #c2410c 0%, #ea580c 100%)',
    darkGradient: 'linear-gradient(135deg, #7c2d12 0%, #9a3412 100%)',
    name: 'Copper',
  },
} as const;

export type PageKey = keyof typeof pageColors;

/**
 * Get color config for a given route path
 */
export function getPageColors(pathname: string): PageColorConfig {
  if (pathname === '/') return pageColors.home;
  if (pathname.startsWith('/about')) return pageColors.about;
  if (pathname.startsWith('/music')) return pageColors.music;
  if (pathname.startsWith('/blog')) return pageColors.blog;
  if (pathname.startsWith('/quotes')) return pageColors.quotes;
  if (pathname.startsWith('/projects')) return pageColors.projects;
  if (pathname.startsWith('/prodeng')) return pageColors.prodeng;
  if (pathname.startsWith('/uses')) return pageColors.uses;
  if (pathname.startsWith('/privacy-policy')) return pageColors.home;
  if (pathname.startsWith('/terms-of-service')) return pageColors.home;
  return pageColors.home;
}

/**
 * Pick between primary (light mode) and accent (dark mode) for text on a page background.
 */
export function getAccentColor(pathname: string, mode: 'light' | 'dark'): string {
  const colors = getPageColors(pathname);
  return mode === 'dark' ? colors.accent : colors.primary;
}

/**
 * Get the gradient for a page, darkened in dark mode so hero sections
 * don't blast bright color against the dark surrounding UI.
 */
export function getGradient(colors: PageColorConfig, mode: 'light' | 'dark'): string {
  return mode === 'dark' ? colors.darkGradient : colors.gradient;
}

/**
 * Get a gradient suitable for text fill (background-clip: text).
 * In dark mode, uses the brighter accent color so text is visible on dark surfaces.
 */
export function getTextGradient(colors: PageColorConfig, mode: 'light' | 'dark'): string {
  if (mode === 'dark') {
    return `linear-gradient(135deg, ${colors.accent} 0%, ${colors.secondary} 100%)`;
  }
  return colors.gradient;
}

/**
 * Flat color palette for Tailwind CSS config
 */
export const tailwindColors = {
  'page-home-primary': pageColors.home.primary,
  'page-home-secondary': pageColors.home.secondary,
  'page-about-primary': pageColors.about.primary,
  'page-about-secondary': pageColors.about.secondary,
  'page-music-primary': pageColors.music.primary,
  'page-music-secondary': pageColors.music.secondary,
  'page-blog-primary': pageColors.blog.primary,
  'page-blog-secondary': pageColors.blog.secondary,
  'page-quotes-primary': pageColors.quotes.primary,
  'page-quotes-secondary': pageColors.quotes.secondary,
  'page-projects-primary': pageColors.projects.primary,
  'page-projects-secondary': pageColors.projects.secondary,
  'page-prodeng-primary': pageColors.prodeng.primary,
  'page-prodeng-secondary': pageColors.prodeng.secondary,
  'page-uses-primary': pageColors.uses.primary,
  'page-uses-secondary': pageColors.uses.secondary,
} as const;
